/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Pagination from "react-js-pagination";
import moment from "moment";
import { FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import SectionTitle from "../../components/section-title";
import Widget from "../../components/widget";
import Modal2 from "../../components/confirm-delete-modal/modal-2";
import PageSizeDropdown from "../../components/PageSizeDropdown";
import ApiClient from "../../apis/apiClient";
import {
  API_BlogDelete,
  API_BlogCategories,
  API_BlogList,
  API_BlogUpdate,
} from "../../apis/constant";
import { checkAccess } from "../../utils/common";
import BlogContentPreview from "../../components/blog/BlogContentPreview";
import { getBlogId } from "../../components/blog/BlogForm";
import { getBlogCategoryLabelFromBlog } from "../../components/blog/blogCategoryOptions";

const FILE_URL = process.env.REACT_APP_FILE_URL || "";

const normalizeListResponse = (payload, fallbackLimit, fallbackPage) => {
  const data = payload?.data;
  const docs = Array.isArray(data)
    ? data
    : data?.docs || data?.results || data?.data || [];

  return {
    docs,
    limit: data?.limit || fallbackLimit,
    page: data?.page || fallbackPage,
    total: data?.total || data?.totalResults || docs.length,
    pages: data?.pages || data?.totalPages || 1,
  };
};

const statusLabels = {
  0: "Draft",
  1: "Published",
};

const getCoverImageUrl = (coverImage) => {
  if (!coverImage) return "";
  if (/^(https?:|blob:)/.test(coverImage)) return coverImage;

  return `${FILE_URL}${coverImage}`;
};

const linkButtonStyle = {
  background: "transparent",
  border: 0,
  padding: 0,
  cursor: "pointer",
};

const BlogList = () => {
  const adminToken = localStorage.getItem("wed_admin_token");
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchBy, setSearchBy] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    total: 0,
    pages: 1,
  });
  const [selectedPreviewBlog, setSelectedPreviewBlog] = useState(null);

  const queryString = useMemo(() => {
    const query = new URLSearchParams();
    query.set("page", pagination.page);
    query.set("limit", pagination.limit);
    if (searchBy) query.set("searchBy", searchBy);
    if (category) query.set("category", category);
    if (status !== "") query.set("status", status);
    return query.toString();
  }, [category, pagination.limit, pagination.page, searchBy, status]);

  const getBlogs = async () => {
    setLoading(true);
    const res = await ApiClient.get(
      `${API_BlogList}?${queryString}`,
      "",
      `Bearer ${adminToken}`,
    );

    if (res?.data?.code === 200) {
      const normalized = normalizeListResponse(
        res.data,
        pagination.limit,
        pagination.page,
      );
      setBlogs(normalized.docs);
      setPagination((prev) => ({
        ...prev,
        limit: normalized.limit,
        page: normalized.page,
        total: normalized.total,
        pages: normalized.pages,
      }));
    } else {
      setBlogs([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const getCategoryOptions = async () => {
      if (!adminToken) return;

      try {
        const res = await ApiClient.get(
          API_BlogCategories,
          "",
          `Bearer ${adminToken}`,
        );
        const backendCategories = Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
        setCategoryOptions(backendCategories);
      } catch (error) {
        setCategoryOptions([]);
      }
    };

    getCategoryOptions();
  }, [adminToken]);

  useEffect(() => {
    if (!adminToken) return;

    const timer = setTimeout(() => {
      getBlogs();
    }, 300);

    return () => clearTimeout(timer);
  }, [adminToken, queryString]);

  const resetPage = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusToggle = async (blog) => {
    const id = getBlogId(blog);
    const nextStatus = Number(blog.status) === 1 ? 0 : 1;
    const res = await ApiClient.patch(
      API_BlogUpdate + id,
      { status: nextStatus },
      `Bearer ${adminToken}`,
    );

    if (res?.data?.code === 200) {
      toast.success(res.data.message || "Status updated successfully");
      getBlogs();
    } else {
      toast.error(res?.data?.message || "Unable to update status");
    }
  };

  const deleteBlog = async (blog) => {
    const id = getBlogId(blog);
    const res = await ApiClient.delete(
      API_BlogDelete + id,
      "",
      `Bearer ${adminToken}`,
    );

    if (res?.data?.code === 200) {
      toast.success(res.data.message || "Blog post deleted successfully");
      getBlogs();
    } else {
      toast.error(res?.data?.message || "Unable to delete blog post");
    }
  };

  if (adminToken === null) {
    return <Redirect to="/" />;
  }

  return (
    <>
      <SectionTitle title="Manage Blogs" subtitle="Blogs" />
      <Widget>
        <div className="flex flex-wrap items-center justify-between mb-4">
          <div className="flex flex-wrap items-center">
            <input
              className="searchCss"
              type="text"
              placeholder="Search title"
              value={searchBy}
              onChange={(e) => {
                setSearchBy(e.target.value);
                resetPage();
              }}
            />
            <select
              className="form-select mx-2 my-2"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                resetPage();
              }}
            >
              <option value="">All Categories</option>
              {categoryOptions.map((categoryOption) => (
                <option key={categoryOption.value} value={categoryOption.value}>
                  {categoryOption.label}
                </option>
              ))}
            </select>
            <select
              className="form-select mx-2 my-2"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                resetPage();
              }}
            >
              <option value="">All Statuses</option>
              <option value="0">Draft</option>
              <option value="1">Published</option>
            </select>
          </div>
          {checkAccess(33, "add") && (
            <Link
              to="/blog/create"
              className="btn btn-default bg-blue-500 hover:bg-blue-600 text-white btn-rounded"
            >
              Add Blog
            </Link>
          )}
        </div>

        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Published At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : blogs.length > 0 ? (
                blogs.map((blog) => {
                  const id = getBlogId(blog);
                  const isPublished = Number(blog.status) === 1;

                  return (
                    <tr key={id}>
                      <td>{blog.title}</td>
                      <td>{getBlogCategoryLabelFromBlog(blog, "-", categoryOptions)}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleStatusToggle(blog)}
                          disabled={!checkAccess(33, "edit")}
                          className={`btn btn-sm btn-rounded ${
                            isPublished
                              ? "bg-green-500 text-white"
                              : "bg-grey-300 text-grey-900"
                          }`}
                        >
                          {statusLabels[blog.status] || "Draft"}
                        </button>
                      </td>
                      <td>
                        {blog.publishedAt
                          ? moment(blog.publishedAt).format("DD-MMM-YYYY HH:mm")
                          : "-"}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="underLineText"
                          style={linkButtonStyle}
                          onClick={() => setSelectedPreviewBlog(blog)}
                        >
                          Preview
                        </button>
                        {checkAccess(33, "edit") && (
                          <>
                            {" | "}
                            <Link
                              to={`/blog/edit/${id}`}
                              className="underLineText"
                            >
                              Edit
                            </Link>
                          </>
                        )}
                        {checkAccess(33, "delete") && (
                          <>
                            {" | "}
                            <Modal2
                              title="Delete"
                              icon={
                                <span className="h-10 w-10 bg-red-100 text-white flex items-center justify-center rounded-full text-lg font-display font-bold">
                                  <FiX
                                    size={18}
                                    className="stroke-current text-red-500"
                                  />
                                </span>
                              }
                              body={
                                <div className="text-sm text-grey-500">
                                  Are You Sure! You want to delete this Blog?
                                </div>
                              }
                              buttonTitle="Confirm"
                              onClick={() => deleteBlog(blog)}
                              buttonClassName="underLineText"
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center" }}>
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && blogs.length > 0 && (
          <div className="flex flex-row items-center justify-between my-4 flex-wrap">
            <Pagination
              activePage={pagination.page}
              itemsCountPerPage={pagination.limit}
              totalItemsCount={pagination.total}
              pageRangeDisplayed={5}
              onChange={(page) => setPagination((prev) => ({ ...prev, page }))}
            />
            <select
              className="form-select text-sm bg-white dark:bg-grey-800 dark:border-grey-800 outline-none shadow-none focus:shadow-none"
              value={pagination.limit}
              onChange={(e) =>
                setPagination((prev) => ({
                  ...prev,
                  limit: Number(e.target.value),
                  page: 1,
                }))
              }
            >
              <PageSizeDropdown />
            </select>
          </div>
        )}
      </Widget>
      <Dialog
        open={Boolean(selectedPreviewBlog)}
        onClose={() => setSelectedPreviewBlog(null)}
        fullScreen
      >
        <DialogTitle>Blog Preview</DialogTitle>
        <DialogContent
          className="blog-preview-dialog-content"
          dividers
          sx={{ backgroundColor: "#f8fafc", padding: 0, scrollBehavior: "smooth" }}
        >
          {selectedPreviewBlog && (
            <BlogContentPreview
              blog={selectedPreviewBlog}
              coverImage={getCoverImageUrl(selectedPreviewBlog.coverImage)}
              categoryOptions={categoryOptions}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <button
            type="button"
            onClick={() => setSelectedPreviewBlog(null)}
            className="btn btn-default btn-rounded bg-white hover:bg-grey-100 text-grey-900 border border-grey-200"
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BlogList;
